import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Chip, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Tabs, Tab, Card, CardContent, Alert, CircularProgress, Tooltip,
  Badge, Divider, GlobalStyles
} from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Medication as MedicationIcon,
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  Comment as CommentIcon
} from '@mui/icons-material';
import { exportToICSR } from '../services/icsrExportApi';
import Snackbar from './Snackbar';
import {
  listCases, 
  updateCase, 
  deleteCase, 
  exportCase,
  getCaseDetail,
  listCaseComments,
  addCaseComment,
  LiteratureCase 
} from '../services/caseManagementApi';
import ArticleReviewEditor from './ArticleReviewEditor';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CaseManagement: React.FC = () => {
  const [cases, setCases] = useState<LiteratureCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState<LiteratureCase | null>(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState<Partial<LiteratureCase>>({});
  const [tabValue, setTabValue] = useState(0);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  // Review editor state
  const [reviewEditorOpen, setReviewEditorOpen] = useState(false);
  const [exportingId, setExportingId] = useState<number | null>(null);

  useEffect(() => {
    loadCases();
  }, []);

  const loadCases = async () => {
    setLoading(true);
    try {
      const data = await listCases();
      setCases(data);
    } catch (error) {
      console.error('Failed to load cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCase = async (caseItem: LiteratureCase) => {
    // Open dialog immediately for responsiveness
    setSelectedCase(caseItem);
    setDetailDialog(true);
    // Load details to enrich with narrative/summary/etc.
    try {
      const detail = await getCaseDetail(caseItem.id);
      const enriched: any = { ...caseItem };
      if (detail?.case) {
        Object.assign(enriched, detail.case);
      }
      // Also map convenience keys if present
      if (typeof detail?.ai_summary === 'string') enriched.ai_summary = detail.ai_summary;
      if (typeof detail?.status === 'string') enriched.status = detail.status;
      if (typeof detail?.final_verdict === 'string') enriched.final_verdict = detail.final_verdict;
      // Narrative may live under detail.case.case_narrative
      if (detail?.case?.case_narrative) enriched.case_narrative = detail.case.case_narrative;
      // If narrative missing, try to create from abstract/snippet and persist
      if (!enriched.case_narrative) {
        const abstractText = (detail as any)?.abstract
          || (detail as any)?.article_snippet
          || (detail as any)?.snippet
          || (detail as any)?.article?.snippet;
        if (abstractText && typeof abstractText === 'string' && abstractText.trim().length > 0) {
          const generated = abstractText.trim();
          enriched.case_narrative = generated;
          try {
            await updateCase(caseItem.id, { case_narrative: generated });
          } catch (e) {
            console.warn('Failed to persist generated narrative:', e);
          }
        }
      }
      setSelectedCase(enriched);
    } catch (e) {
      console.error('Failed to load case detail:', e);
    }
    // Load comments
    try {
      const comments = await listCaseComments(caseItem.id);
      setComments(comments);
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const handleEditCase = (caseItem: LiteratureCase) => {
    setSelectedCase(caseItem);
    setEditForm(caseItem);
    setEditDialog(true);
  };

  const handleReviewCase = async (caseItem: LiteratureCase) => {
    setSelectedCase(caseItem);
    setReviewEditorOpen(true);
  };

  const handleSaveReview = async (data: Partial<LiteratureCase>) => {
    if (!selectedCase) return;
    try {
      await updateCase(selectedCase.id, data);
      loadCases(); // Refresh the cases list
    } catch (error) {
      console.error('Failed to save review:', error);
      throw error;
    }
  };

  const handleAddReviewComment = async (caseId: number, comment: string) => {
    try {
      const newComment = await addCaseComment(caseId, comment);
      return newComment;
    } catch (error) {
      console.error('Failed to add comment:', error);
      throw error;
    }
  };

  const handleLoadReviewComments = async (caseId: number) => {
    try {
      const comments = await listCaseComments(caseId);
      return comments;
    } catch (error) {
      console.error('Failed to load comments:', error);
      throw error;
    }
  };

  const canExport = (c: LiteratureCase) => {
    // A case can be exported only if it has been reviewed and has NOT been exported already.
    const isReviewed = (c.final_verdict && c.final_verdict !== '') || c.status === 'completed';
    const isAlreadyExported = c.status === 'exported';
    return isReviewed && !isAlreadyExported;
  };

  const handleExportToICSR = async (c: LiteratureCase) => {
    setExportingId(c.id);
    try {
      // fetch article details for richer export payload
      const detail = await getCaseDetail(c.id);
      const res = await exportToICSR(c, detail);
      if (res.success) {
        // Wait a moment for DB to commit, then refresh to show exported status
        setTimeout(() => {
          loadCases();
        }, 300);
        setSnackbar({ message: `Exported to ICSR: ${res.icsrId || ''}. Refreshing list...`, type: 'success' });
        try {
          const rid = res.icsrId ? `record_id=${encodeURIComponent(res.icsrId)}` : '';
          const ae = res.adverse_event_id ? `&ae_id=${encodeURIComponent(String(res.adverse_event_id))}` : '';
          const sep = rid ? '&' : '';
          // Navigate to dashboard ICSR tab with identifiers to force-include
          window.location.href = `/dashboard?tab=adverse-events${rid || ae ? `&${rid}${rid ? ae : ae.replace(/^&/, '')}` : ''}`;
        } catch {}
      } else {
        setSnackbar({ message: res.message || 'Failed to export', type: 'error' });
      }
    } catch (e) {
      console.error('Export to ICSR failed:', e);
      setSnackbar({ message: 'Export failed', type: 'error' });
    } finally {
      setExportingId(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedCase) return;
    try {
      await updateCase(selectedCase.id, editForm);
      await loadCases();
      setEditDialog(false);
    } catch (error) {
      console.error('Failed to update case:', error);
    }
  };

  const handleDeleteCase = async (caseId: number) => {
    if (window.confirm('Are you sure you want to delete this case?')) {
      try {
        await deleteCase(caseId);
        await loadCases();
      } catch (error) {
        console.error('Failed to delete case:', error);
      }
    }
  };

  const handleExportCase = async (caseId: number) => {
    try {
      const data = await exportCase(caseId);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `case-${caseId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export case:', error);
    }
  };

  const handleAddComment = async () => {
    if (!selectedCase || !newComment.trim()) return;
    try {
      const comment = await addCaseComment(selectedCase.id, newComment);
      setComments([...comments, comment]);
      setNewComment('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const getSeverityColor = (severity?: string | null) => {
    switch (severity?.toLowerCase()) {
      case 'serious': return 'error';
      case 'moderate': return 'warning';
      case 'mild': return 'info';
      default: return 'default';
    }
  };

  const renderCaseDetails = () => {
    if (!selectedCase) return null;

    return (
      <>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Patient Info" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Adverse Event" icon={<WarningIcon />} iconPosition="start" />
          <Tab label="Product Info" icon={<MedicationIcon />} iconPosition="start" />
          <Tab label="Reporter" icon={<DescriptionIcon />} iconPosition="start" />
          <Tab label="Assessment" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Comments" icon={<Badge badgeContent={comments.length} color="primary"><CommentIcon /></Badge>} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Patient ID</Typography>
              <Typography>{selectedCase.patient_identifier || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Age</Typography>
              <Typography>{selectedCase.patient_age || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Sex</Typography>
              <Typography>{selectedCase.patient_sex || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Weight</Typography>
              <Typography>{selectedCase.patient_weight || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Height</Typography>
              <Typography>{selectedCase.patient_height || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Pregnancy Status</Typography>
              <Typography>{selectedCase.pregnancy_status || 'N/A'}</Typography>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" color="text.secondary">Medical History</Typography>
              <Typography>{selectedCase.medical_history || 'N/A'}</Typography>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" color="text.secondary">Risk Factors</Typography>
              <Typography>{selectedCase.risk_factors || 'N/A'}</Typography>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Alert severity={getSeverityColor(selectedCase.severity) as any || 'info'} sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Event: {selectedCase.event_verbatim || 'N/A'}</Typography>
                <Typography variant="body2">MedDRA PT: {selectedCase.meddra_pt || 'N/A'}</Typography>
              </Alert>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Start Date</Typography>
              <Typography>{selectedCase.ae_start_date || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Stop Date</Typography>
              <Typography>{selectedCase.ae_stop_date || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
              <Typography>{selectedCase.ae_duration || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Outcome</Typography>
              <Typography>{selectedCase.outcome || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Seriousness</Typography>
              <Chip label={selectedCase.seriousness || 'N/A'} color={getSeverityColor(selectedCase.seriousness) as any} size="small" />
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Severity</Typography>
              <Chip label={selectedCase.severity || 'N/A'} color={getSeverityColor(selectedCase.severity) as any} size="small" />
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Causality</Typography>
              <Typography>{selectedCase.causality || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Dechallenge</Typography>
              <Typography>{selectedCase.dechallenge || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Rechallenge</Typography>
              <Typography>{selectedCase.rechallenge || 'N/A'}</Typography>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="h6" gutterBottom>Suspect Product</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Product Name</Typography>
              <Typography>{selectedCase.suspect_product_name || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Generic Name</Typography>
              <Typography>{selectedCase.suspect_product_generic || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Dosage Form</Typography>
              <Typography>{selectedCase.dosage_form || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Strength</Typography>
              <Typography>{selectedCase.strength || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Indication</Typography>
              <Typography>{selectedCase.indication || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Route</Typography>
              <Typography>{selectedCase.route || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Dose</Typography>
              <Typography>{selectedCase.dose || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Start Date</Typography>
              <Typography>{selectedCase.product_start_date || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Stop Date</Typography>
              <Typography>{selectedCase.product_stop_date || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Batch Number</Typography>
              <Typography>{selectedCase.batch_number || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Action Taken</Typography>
              <Typography>{selectedCase.action_taken || 'N/A'}</Typography>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" color="text.secondary">Concomitant Medications</Typography>
              <Typography>{selectedCase.concomitants || 'N/A'}</Typography>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Reporter Name</Typography>
              <Typography>{selectedCase.reporter_name || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Reporter Type</Typography>
              <Typography>{selectedCase.reporter_type || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Occupation</Typography>
              <Typography>{selectedCase.reporter_occupation || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Contact</Typography>
              <Typography>{selectedCase.reporter_contact || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Country</Typography>
              <Typography>{selectedCase.reporter_country || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Relationship to Patient</Typography>
              <Typography>{selectedCase.reporter_relationship || 'N/A'}</Typography>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Manufacturer Information</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Manufacturer Name</Typography>
              <Typography>{selectedCase.manufacturer_name || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Manufacturer Contact</Typography>
              <Typography>{selectedCase.manufacturer_contact || 'N/A'}</Typography>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" color="text.secondary">Case Narrative</Typography>
              <Paper sx={{ p: 2, bgcolor: '#fff', mt: 1 }}>
                <Typography
                  variant="body1"
                  sx={{ color: '#000', whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: '15px' }}
                >
                  {selectedCase.case_narrative || 'N/A'}
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" color="text.secondary">AI Summary</Typography>
              <Paper sx={{ p: 2, bgcolor: 'blue.50', mt: 1 }}>
                <Typography variant="body2">{selectedCase.ai_summary || 'N/A'}</Typography>
              </Paper>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">AI Score</Typography>
              <Chip label={selectedCase.ai_score || 'N/A'} color="primary" />
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Status</Typography>
              <Chip label={selectedCase.status} color={selectedCase.status === 'completed' ? 'success' : 'warning'} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" color="text.secondary">Assessor Comments</Typography>
              <Typography>{selectedCase.assessor_comments || 'N/A'}</Typography>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" color="text.secondary">Final Verdict</Typography>
              <Typography>{selectedCase.final_verdict || 'N/A'}</Typography>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Stack spacing={2}>
            {comments.map((comment) => (
              <Card key={comment.id} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">{comment.user_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(comment.created_at).toLocaleString()}
                    </Typography>
                  </Stack>
                  <Typography variant="body2">{comment.comment_text}</Typography>
                </CardContent>
              </Card>
            ))}
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <Button variant="contained" onClick={handleAddComment} disabled={!newComment.trim()}>
                Add
              </Button>
            </Stack>
          </Stack>
        </TabPanel>

        {/* Removed extra Narrative tab to avoid duplication; Narrative lives under Assessment */}
      </>
    );
  };

  // ---------- MRT Columns (front-facing essentials only) ----------
  const columns = useMemo<MRT_ColumnDef<LiteratureCase>[]>(() => ([
    // Core identifiers
    { accessorKey: 'id', header: 'ID', size: 70, enableColumnFilter: false, enableSorting: true },
    { accessorKey: 'article_title', header: 'Article', size: 280, Cell: ({ cell }) => (
      <Tooltip title={cell.getValue<string>() || ''}>
        <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>{cell.getValue<string>() || 'N/A'}</Typography>
      </Tooltip>
    ) },
    // Patient
    { accessorKey: 'patient_identifier', header: 'Patient ID', size: 120 },
    // AE core fields aligned with extractedFields
    { accessorKey: 'event_verbatim', header: 'Event', size: 220, Cell: ({ cell }) => (
      <Tooltip title={cell.getValue<string>() || ''}>
        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{cell.getValue<string>() || 'N/A'}</Typography>
      </Tooltip>
    ) },
    { accessorKey: 'meddra_pt', header: 'MedDRA PT', size: 140 },
    // Product
    { accessorKey: 'suspect_product_name', header: 'Product', size: 170 },
    // Outcome/Seriousness/Severity
    { accessorKey: 'outcome', header: 'Outcome', size: 120 },
    { accessorKey: 'severity', header: 'Severity', size: 100, Cell: ({ cell }) => (
      <Chip size="small" label={cell.getValue<string>() || 'N/A'} color={getSeverityColor(cell.getValue<string>()) as any} />
    ) },
    { accessorKey: 'seriousness', header: 'Seriousness', size: 110 },
    // Process/status
    { accessorKey: 'status', header: 'Status', size: 110, Cell: ({ cell }) => (
      <Chip size="small" label={cell.getValue<string>() || 'N/A'} color={cell.getValue<string>() === 'completed' ? 'success' : 'warning'} />
    ) },
    { accessorKey: 'ai_score', header: 'AI', size: 70 },
    { accessorKey: 'created_at', header: 'Created', size: 170 },
    { accessorKey: 'final_verdict', header: 'Verdict', size: 160 },
  ]), []);

  // ---------- CSV Export (no external dep) ----------
  const toCSV = (rows: any[]) => {
    if (!rows || rows.length === 0) return '';
    const headers = Object.keys(rows[0]);
    const esc = (v: any) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const lines = [headers.join(',')].concat(rows.map(r => headers.map(h => esc(r[h])).join(',')));
    return lines.join('\n');
  };
  const downloadCSV = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAll = () => {
    const csv = toCSV(cases);
    downloadCSV(csv, `cases-all-${Date.now()}.csv`);
  };
  const handleExportRows = (rows: LiteratureCase[]) => {
    const csv = toCSV(rows);
    downloadCSV(csv, `cases-rows-${Date.now()}.csv`);
  };

  const table = useMaterialReactTable<LiteratureCase>({
    columns,
    data: loading ? [] : cases,
    enableRowSelection: true,
    enableColumnFilters: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    enablePinning: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Actions',
        size: 220, // allow space for all icons
      },
    },
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiTableContainerProps: { sx: { borderRadius: 1, border: '1px solid', borderColor: 'divider' } },
    muiTableHeadCellProps: {
      sx: {
        bgcolor: 'action.hover',
        color: 'text.primary',
        fontWeight: 600,
        borderBottom: '1px solid',
        borderColor: 'divider',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
      },
    },
    muiTableProps: {
      sx: {
        '& tbody tr:nth-of-type(odd)': {
          bgcolor: 'action.hover',
        },
      },
    },
    initialState: { density: 'compact' },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 2, p: 1, flexWrap: 'wrap' }}>
        <Button size="small" onClick={handleExportAll} variant="outlined" color="primary">Export All</Button>
        <Button
          size="small"
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          onClick={() => handleExportRows(table.getPrePaginationRowModel().rows.map(r => r.original))}
          variant="outlined"
          color="primary"
        >
          Export All Rows
        </Button>
        <Button
          size="small"
          disabled={table.getRowModel().rows.length === 0}
          onClick={() => handleExportRows(table.getRowModel().rows.map(r => r.original))}
          variant="outlined"
          color="primary"
        >
          Export Page Rows
        </Button>
        <Button
          size="small"
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
          onClick={() => handleExportRows(table.getSelectedRowModel().rows.map(r => r.original))}
          variant="outlined"
          color="primary"
        >
          Export Selected
        </Button>
      </Box>
    ),
    renderRowActions: ({ row }) => (
      <Stack direction="row" spacing={1}>
        <IconButton size="small" color="primary" onClick={() => handleReviewCase(row.original)} title="Review & Highlight">
          <AssessmentIcon />
        </IconButton>
        <IconButton size="small" color="info" onClick={() => handleViewCase(row.original)} title="Open Document Editor">
          <ViewIcon />
        </IconButton>
        <IconButton size="small" color="secondary" onClick={() => handleEditCase(row.original)} title="Edit Case Fields">
          <EditIcon />
        </IconButton>
        <IconButton size="small" color="success" onClick={() => handleExportCase(row.original.id)} title="Download JSON">
          <DownloadIcon />
        </IconButton>
        <Tooltip title={canExport(row.original) ? 'Export to ICSR' : 'Export disabled until reviewed'}>
          <span>
            <IconButton size="small" color="primary" onClick={() => handleExportToICSR(row.original)} disabled={!canExport(row.original) || exportingId === row.original.id}>
              <HospitalIcon />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" color="error" onClick={() => handleDeleteCase(row.original.id)} title="Delete">
          <DeleteIcon />
        </IconButton>
      </Stack>
    ),
  });

  return (
    <Box sx={{ p: 3 }}>
      {snackbar && <Snackbar message={snackbar.message} type={snackbar.type} onClose={() => setSnackbar(null)} />}
      <GlobalStyles styles={{
        '.highlight-term': {
          backgroundColor: '#fff59d',
          borderBottom: '2px solid #fbc02d',
        },
      }} />
      <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" color="text.primary" sx={{ fontWeight: 600 }}>📋 Case Management</Typography>
          <Chip label={`${cases.length} Cases`} color="primary" />
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <MaterialReactTable table={table} />
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ color: 'text.primary', fontWeight: 600 }}>
          Case Details - ID: {selectedCase?.id}
        </DialogTitle>
        <DialogContent>
          {renderCaseDetails()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: 'text.primary', fontWeight: 600 }}>Edit Case</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Status"
              value={editForm.status || ''}
              onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              select
              SelectProps={{ native: true }}
            >
              <option value="pending">Pending</option>
              <option value="in_review">In Review</option>
              <option value="completed">Completed</option>
            </TextField>
            <TextField
              label="Final Verdict"
              value={editForm.final_verdict || ''}
              onChange={(e) => setEditForm({ ...editForm, final_verdict: e.target.value })}
              multiline
              rows={3}
            />
            <TextField
              label="Assessor Comments"
              value={editForm.assessor_comments || ''}
              onChange={(e) => setEditForm({ ...editForm, assessor_comments: e.target.value })}
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* New Article Review Editor */}
      <ArticleReviewEditor
        open={reviewEditorOpen}
        onClose={() => setReviewEditorOpen(false)}
        caseData={selectedCase}
        onSave={handleSaveReview}
        onAddComment={handleAddReviewComment}
        onLoadComments={handleLoadReviewComments}
      />
    </Box>
  );
};

export default CaseManagement;
